import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { type ISolutionDeploymentListItem } from '../../interfaces/ISolutionDeployment';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { classNames } from '../../utilities/styleUtils';
import StatusPill from '../common/StatusPill';
import ProjectIcon from '../common/ProjectIcon';
import { useSolution } from '../../hooks/useSolution';
import { useAppDispatch } from '../../hooks';
import { fetchSolution } from '../../reducers/solutionsSlice';
import { useToken } from '../../hooks/useToken';
import { getDeploymentStatusColorVariant } from '../../utilities/solutionDeploymentUtils';
import { getSolutionPath } from '../../utilities/solutionUtils';

interface SolutionDemoItemProps {
  deployment: ISolutionDeploymentListItem;
  accountName: string;
  mini?: boolean;
}

export default function SolutionDemoItem({ deployment, accountName, mini = false }: SolutionDemoItemProps) {
  const dispatch = useAppDispatch();
  const token = useToken();
  const navigate = useNavigate();
  const solutionId = deployment.project_id.toString();
  const { solution } = useSolution(solutionId);

  // Fetch solution details if not already loaded
  useEffect(() => {
    if (solution?.listing?.name === '') {
      dispatch(fetchSolution({ token, fullPath: `/solutions/${solutionId}`, iid: solutionId })).catch((err) => {
        console.log('Fetch solution error:', err);
      });
    }
  }, [solution?.listing?.name, dispatch, token, solutionId]);

  // Handle click on the entire item
  const handleItemClick = () => {
    navigate(`/deployments/${deployment.id}`);
  };

  // Determine the background and border colors based on the deployment status
  const getDeploymentStyles = () => {
    const status = deployment.status.toLowerCase();
    switch (status) {
      case 'ready':
      case 'creating':
      case 'updating':
      case 'deleting':
      case 'pending':
      case 'errored':
        return 'bg-white';
      case 'deleted':
        return 'bg-gray-100';
      default:
        return 'bg-white';
    }
  };

  // Determine the hover style based on the deployment status
  const getHoverStyle = () => {
    const status = deployment.status.toLowerCase();
    switch (status) {
      case 'ready':
        return 'hover:bg-gray-50';
      case 'creating':
      case 'updating':
      case 'deleting':
      case 'pending':
      case 'errored':
      case 'deleted':
      default:
        return 'hover:bg-gray-200';
    }
  };

  // Render mini version
  if (mini) {
    return (
      <div
        onClick={handleItemClick}
        className={classNames(
          `relative flex items-center justify-between px-3 py-2 shadow-sm ring-1 ring-gray-900/5 rounded-sm cursor-pointer`,
          getDeploymentStyles(),
          getHoverStyle(),
        )}
      >
        <div className="flex flex-col overflow-hidden flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">{deployment.solution_prefix}</p>
          <p className="text-xs text-gray-500 truncate">{accountName}</p>
        </div>
        <ChevronRightIcon className="w-5 h-5 text-gray-400 flex-shrink-0 ml-2" />
      </div>
    );
  }

  // Render full version
  return (
    <div
      onClick={handleItemClick}
      className={classNames(
        `relative grid grid-cols-12 gap-x-4 px-3 py-1 shadow-sm ring-1 ring-gray-900/5 sm:rounded-sm cursor-pointer`,
        getDeploymentStyles(),
        getHoverStyle(),
      )}
    >
      <div className="hidden sm:flex col-span-1 items-center h-12">
        <StatusPill status={deployment.status} colorVariant={getDeploymentStatusColorVariant(deployment.status)} />
      </div>
      <div className="flex sm:hidden col-span-1 items-center">
        <StatusPill
          status={deployment.status}
          colorVariant={getDeploymentStatusColorVariant(deployment.status)}
          micro
        />
      </div>
      <div className="col-span-10 lg:col-span-3 md:col-span-4 sm:col-span-5 flex items-center justify-between text-sm">
        <div className="flex flex-col items-left justify-left overflow-hidden w-full">
          <p className="text-sm font-semibold text-gray-900 truncate" title={deployment.solution_prefix}>
            {deployment.solution_prefix}
          </p>
        </div>
      </div>
      <div className="hidden lg:col-span-3 md:col-span-3 md:flex items-center justify-left text-sm overflow-hidden">
        <span className="truncate" title={accountName}>
          {accountName}
        </span>
      </div>
      <div className="hidden lg:col-span-3 md:col-span-2 sm:col-span-3 sm:flex items-center justify-left text-sm space-x-2 overflow-hidden">
        <ProjectIcon iconSrc={solution?.avatarUrl ?? solution?.listing?.icon} size="size-8" />
        {solution?.listing?.name ? (
          <Link
            to={getSolutionPath(solutionId, solution)}
            className="truncate text-dataops-yale-blue hover:underline"
            title={solution.listing.name}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {solution.listing.name}
          </Link>
        ) : (
          <span className="truncate" title={`Project ${deployment.project_id}`}>
            Project {deployment.project_id}
          </span>
        )}
      </div>
      <div className="hidden lg:col-span-1 lg:flex items-center justify-left text-sm">
        <span className="text-xs text-gray-500 truncate">{new Date(deployment.created_at).toLocaleDateString()}</span>
      </div>
      <div className="hidden md:flex items-center justify-right justify-self-end text-sm">
        <ChevronRightIcon className="w-5 h-5 text-gray-400" />
      </div>
    </div>
  );
}

export function SolutionDemoItemHeader() {
  return (
    <div className="relative grid grid-cols-12 gap-x-4 px-2 py-2">
      <div className="hidden sm:flex col-span-1">Status</div>
      <div className="col-span-11 lg:col-span-3 md:col-span-4 sm:col-span-5 flex items-center justify-left text-sm">
        Name
      </div>
      <div className="hidden lg:col-span-3 md:col-span-3 md:flex items-center justify-left text-sm">
        Snowflake Account
      </div>
      <div className="hidden lg:col-span-3 md:col-span-2 sm:col-span-3 sm:flex items-center justify-left text-sm">
        Solution
      </div>
      <div className="hidden lg:col-span-1 lg:flex items-center justify-left text-sm">Created</div>
    </div>
  );
}
